/** DPR / DSR personnel role helpers — driven by `pih.role.personnelRoleType` from API. */

import { getRoleModuleType, isBothModuleType, roleMatchesPersonnelModule } from '../services/personnelRoleService';

export { getRoleModuleType, isBothModuleType, roleMatchesPersonnelModule };

const normalizeRoleName = (name) => String(name || '').trim().toLowerCase();

const getRoleDisplayName = (role) => String(role?.roleName ?? role?.name ?? '').trim();

export const isActiveRole = (role) =>
  String(role?.status || 'active').trim().toLowerCase() !== 'inactive';

export const getActiveRolesList = (roles = []) => (roles || []).filter(isActiveRole);

export const isRoleAllowedForPersonnelModule = (role, personnelRoleType) => {
  if (!isActiveRole(role)) return false;
  return roleMatchesPersonnelModule(role, personnelRoleType);
};

/** Active role names for a personnel module (`welding` | `coating` | `both`). Roles typed `both` appear in DPR and DSR pickers. */
export const getPersonnelRoleNames = (roles, personnelRoleType) =>
  (roles || [])
    .filter((role) => isRoleAllowedForPersonnelModule(role, personnelRoleType))
    .map(getRoleDisplayName)
    .filter(Boolean);

/** Allowed roles for DPR personnel picker (welding module). */
export const getDprPersonnelAllowedRoles = (roles = []) => getPersonnelRoleNames(roles, 'welding');

/** Allowed job titles for DSR technician log (coating module). */
export const getDsrTechnicianAllowedRoles = (roles = []) => getPersonnelRoleNames(roles, 'coating');

const getAllowedPersonnelTypesForProject = (projectType) => {
  const pt = String(projectType || '').trim().toLowerCase();
  if (pt === 'coating') return new Set(['coating']);
  if (pt === 'welding') return new Set(['welding']);
  return new Set(['welding', 'coating']);
};

/** Filter active roles for project team / manpower by project discipline. */
export const filterRolesForProjectType = (roles, projectType) =>
  (roles || []).filter((role) => {
    if (!isActiveRole(role)) return false;
    if (isBothModuleType(role)) return true;
    const type = getRoleModuleType(role);
    if (type == null) return true;
    return getAllowedPersonnelTypesForProject(projectType).has(type);
  });

/** Default personnelRoleType when adding a role inline from project context. */
export const getDefaultPersonnelRoleTypeForProject = (projectType) => {
  const pt = String(projectType || '').trim().toLowerCase();
  if (pt === 'coating') return 'coating';
  if (pt === 'welding') return 'welding';
  if (pt === 'both') return 'both';
  return null;
};

export const filterRolesByDprAllowList = (roles, allowedRoleNames) => {
  const allowed = new Set((allowedRoleNames || []).map(normalizeRoleName));
  return (roles || []).filter((role) => {
    const name = role?.roleName ?? role?.name ?? '';
    return allowed.has(normalizeRoleName(name));
  });
};

/** Merge API roles with the allow list so every permitted title appears in the picker. */
export const buildDprPersonnelRoleOptions = (roles, allowedRoleNames) => {
  const fromApi = filterRolesByDprAllowList(roles, allowedRoleNames);
  const byName = new Map();

  fromApi.forEach((role) => {
    const name = String(role?.roleName ?? role?.name ?? '').trim();
    if (name) byName.set(normalizeRoleName(name), role);
  });

  (allowedRoleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = normalizeRoleName(trimmed);
    if (!byName.has(key)) {
      byName.set(key, { roleName: trimmed, roleId: null });
    }
  });

  return [...byName.values()];
};

/**
 * Keep allow-list roles and also surface custom / in-use roles (e.g. added via POST /role).
 */
export const mergeCustomRoleOptions = ({
  baseOptions = [],
  allRoles = [],
  activeRoleNames = [],
  isPendingRole,
  getName = (role) => String(role?.roleName ?? role?.name ?? '').trim(),
}) => {
  const baseKeys = new Set((baseOptions || []).map((role) => normalizeRoleName(getName(role))));
  const extras = [];

  const addByName = (name, roleObj) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = normalizeRoleName(trimmed);
    if (baseKeys.has(key)) return;
    baseKeys.add(key);
    extras.push(roleObj ?? { roleName: trimmed, roleId: null });
  };

  (activeRoleNames || []).forEach((name) => addByName(name));

  (allRoles || []).forEach((role) => {
    const name = getName(role);
    if (!name) return;
    const key = normalizeRoleName(name);
    if (baseKeys.has(key)) return;
    if (isPendingRole?.(role) || (activeRoleNames || []).some((n) => normalizeRoleName(n) === key)) {
      addByName(name, role);
    }
  });

  return [...baseOptions, ...extras];
};

/** Build sorted string role options for DSR technician log (coating titles only). */
export const buildDsrTechnicianRoleOptions = (roleNames, allowedRoleNames, extraRoleNames = []) => {
  const allowed = new Set((allowedRoleNames || []).map(normalizeRoleName));
  const byName = new Map();

  (roleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed || !allowed.has(normalizeRoleName(trimmed))) return;
    byName.set(normalizeRoleName(trimmed), trimmed);
  });

  (allowedRoleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = normalizeRoleName(trimmed);
    if (!byName.has(key)) byName.set(key, trimmed);
  });

  (extraRoleNames || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed || trimmed === 'Select Role') return;
    byName.set(normalizeRoleName(trimmed), trimmed);
  });

  const sorted = [...byName.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  return ['Select Role', ...sorted];
};
